import './bootstrap';
import './utils';

import 'gridstack/dist/gridstack.min.css';
import '../css/app.css';
import '../scss/style.scss';

// Import modules...
import { externalAccessHeaders } from '@/Utils/externalAccessHeaders';
import cTooltip from '@/directives/v-c-tooltip.js';
import { eventBus, events } from '@/eventBus.js';
import dateTimeMedium from '@/filters/dateTimeMedium.js';
import duration from '@/filters/duration.js';
import fullDate from '@/filters/fullDate.js';
import linkify from '@/filters/linkify.js';
import nl2br from '@/filters/nl2br.js';
import relativeDate from '@/filters/relativeDate.js';
import shortDate from '@/filters/shortDate.js';
import time from '@/filters/time.js';
import hasAllPermissions from '@/mixins/hasAllPermissions';
import hasAnyPermission from '@/mixins/hasAnyPermission';
import hasFeature from '@/mixins/hasFeature.js';
import i18n from '@/mixins/i18n.js';
import isFormSubmittable from '@/mixins/isFormSubmittable.js';
import locale from '@/mixins/locale.js';
import user from '@/mixins/user.js';
import setupErrorReporting from '@/setupErrorReporting.js';
import { setupNativeHooks } from '@/shared/native.js';
import CoreuiVue from '@coreui/vue';
import { createInertiaApp, Link, router } from '@inertiajs/vue3';
import { createApp, h } from 'vue';
import { ZiggyVue } from 'ziggy-js';
import { iconsSet as icons } from '../icons/icons.js';

import Column from '@/Components/Column.vue';
import ConnectivityGate from '@/Components/ConnectivityGate.vue';
import Icon from '@/Components/Icon.vue';
import ModalLink from '@/Components/ModalLink.vue';
import NewLineToBreak from '@/Components/NewLineToBreak.vue';
import Row from '@/Components/Row.vue';
import Spinner from '@/Components/Spinner.vue';
import prettyBytes from '@/filters/prettyBytes.js';
import detectSmallScreen from '@/mixins/detectSmallScreen.js';
import '@formatjs/intl-durationformat/polyfill.js';
import { initFromPageProps, ModalRoot } from '@inertiaui/modal-vue';
import Vapor from 'laravel-vapor';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { createPinia } from 'pinia';
import Modal from './Components/Modal.vue';

window.Vapor = Vapor;
Vapor.withBaseAssetUrl(import.meta.env.VITE_VAPOR_ASSET_URL);

const appName = import.meta.env.VITE_APP_NAME || 'curie';

router.on('httpException', (event) => {
    const data = event.detail?.response?.data;
    const message = data?.message ?? data?.error;
    if (data?.status === 'error' && message) {
        event.preventDefault();
        eventBus.$emit(events.presentFlashMessage, {
            payload: { message, status: 'error' },
        });
    }
});

createInertiaApp({
    title: (title) => `${title} - ${appName}`,
    resolve: (name) => resolvePageComponent(`./Pages/${name}.vue`, import.meta.glob(['./Pages/**/*Page.vue', './Pages/Modals/**/*Modal.vue'])),
    setup({ el, App, props, plugin }) {
        initFromPageProps(props);

        const app = createApp({ render: () => h(ConnectivityGate, () => h(ModalRoot, () => h(App, props))) })
            .use(plugin)
            .use(CoreuiVue)
            .use(createPinia())
            .use(ZiggyVue)
            .component('Row', Row)
            .component('Column', Column)
            .component('Icon', Icon)
            .component('Spinner', Spinner)
            .component('Link', Link)
            .component('nl2br', NewLineToBreak)
            .component('ModalLink', ModalLink)
            .component('Modal', Modal)
            .mixin({ methods: { cdnAssetPath: window.Vapor.asset } })
            .mixin(hasAnyPermission)
            .mixin(hasAllPermissions)
            .mixin(locale)
            .mixin(i18n)
            .mixin(hasFeature)
            .mixin(user)
            .mixin(isFormSubmittable)
            .mixin(detectSmallScreen)
            .provide('icons', icons);

        const registeredTooltipDirective = app.directive('c-tooltip');
        if (registeredTooltipDirective) {
            Object.assign(registeredTooltipDirective, cTooltip);
        }

        app.config.globalProperties.$route = route;
        app.config.globalProperties.$filters = {
            fullDate,
            relativeDate,
            dateTimeMedium,
            shortDate,
            duration,
            time,
            linkify,
            nl2br,
            prettyBytes,
        };

        app.mount(el);
        setupErrorReporting(app);
        setupNativeHooks();
        return app;
    },
    defaults: {
        visitOptions: (href, options) => ({
            headers: {
                ...options.headers,
                ...externalAccessHeaders(href),
            },
        }),
    },
    progress: {
        color: '#4B5563',
    },
});
